import React, {useState} from "react";
import {inject, observer} from "mobx-react";
import {useTranslation} from "react-i18next";
import {Form, Formik} from "formik";
import {RemindersModal} from "./RemindersModal";
import {OffersSwitchOffModal} from "./OffersSwitchOffModal";
import CustomRadioSwitch from "components/CustomRadioSwitch";
import {CustomTooltip} from "components/CustomTooltip";
import {ButtonTransparent} from "components/buttons/ButtonTransparent";
import {
  IconNotificationCircle,
  IconCheck,
  IconEyeCrossed,
  IconLock,
  IconBell,
  IconCog,
  IconBullHorn,
  IconRefresh,
} from "components/icons";
import {color} from "constants/color.consts";
import {roles} from "constants/auth.const";
import {rfpStatuses} from "constants/buyer.consts";
import {getFullName, hasUserPermission, isObject} from "helpers/helper";
import {dateTimeFormat} from "helpers/datetime";
import {requestSchema} from "./formAttrs";
import MiniSelect from "components/selects/MiniSelect";
import {VideoInstructionLink} from "components/VideoInstructionLink";
import {turnoverMiniSelect} from "constants/select.consts";
import {LockValue} from "components/LockValue";

const UpperRequestSettingsHeader = inject("store")(
  observer(({store: {auth, buyer}}) => {
    const {currentRequest} = buyer;
    const isDraft = currentRequest.status === rfpStatuses.DRAFT;
    const [isRemindersModal, setRemindersModal] = useState(false);
    const [isHardDateCloseOffersSwitchOffModal, setHardDateCloseOffersSwitchOffModal] =
      useState(false);
    const [isHideOffersSwitchOffModal, setHideOffersSwitchOffModal] = useState(false);
    const {t} = useTranslation();

    const historyRow = (user, date, status) =>
      `${t(`CREATE_RFP_HIDE_OFFERS_TOOLTIP_${status ? "ON" : "OFF"}`)} ${getFullName(
        user.firstName,
        user.lastName
      )} | ${dateTimeFormat(date)}`;

    const handleChange = (e, needSaveRequest = false) => {
      buyer.setDraftSavedStatus(false);
      if (isObject(e)) {
        if (
          "offersHidden" in e ||
          "hardDateCloseOffers" in e ||
          "reminders" in e ||
          "reminderDays" in e ||
          "isPublic" in e ||
          "minRevenue" in e
        ) {
          buyer.updateCurrentRequestInfo(e, needSaveRequest);
        }
      }
    };

    const hideOfferSwitchHistory = currentRequest?.OffersHideHistory?.length
      ? currentRequest.OffersHideHistory.map(
          (item, i) =>
            `${i === 0 ? "\n" : ""}${historyRow(
              item.User,
              item.updatedAt,
              item.offersHidden
            )}\n`
        ).join("")
      : "";
    const hideOfferTooltipTextEdit = `${t(
      "CREATE_RFP_HIDE_OFFERS_TOOLTIP_MAIN"
    )}\n${hideOfferSwitchHistory}`;

    const {clientRole} = auth;

    const isOffersHideInfo =
      currentRequest.status === rfpStatuses.PUBLISHED &&
      !hasUserPermission([roles.ADMIN, roles.OWNER], clientRole);

    return (
      <Formik
        enableReinitialize
        initialValues={currentRequest}
        validationSchema={requestSchema}
      >
        {({values, setFieldValue}) => (
          <Form onChange={(e) => handleChange(e)}>
            <div className="main-fields_wrapper__row-info wrappable">
              <div className="toggles">
                <div className="main-fields_wrapper__input hide-offers">
                  <CustomRadioSwitch
                    name="reminders"
                    checked={currentRequest?.reminders}
                    uppercase
                    small
                    addWrapperClass=""
                    isActive={(checked) => {
                      handleChange({reminders: checked});
                      setFieldValue("reminders", checked);
                    }}
                  />
                  <CustomTooltip text="CREATE_RFP_REMEMBER_LABEL">
                    <div className="hide-offers-label-icon">
                      <IconBell />
                    </div>
                  </CustomTooltip>
                  <ButtonTransparent
                    icon={<IconCog />}
                    noBorder
                    tiny
                    addClass="pa-0"
                    onClick={() => setRemindersModal(true)}
                  />
                </div>
                <div className="main-fields_wrapper__input hide-offers">
                  <CustomRadioSwitch
                    name="hardDateCloseOffers"
                    checked={currentRequest?.hardDateCloseOffers}
                    uppercase
                    small
                    addWrapperClass=""
                    isActive={(checked) => {
                      if (checked) {
                        handleChange({hardDateCloseOffers: checked});
                        setFieldValue("hardDateCloseOffers", checked);
                      } else setHardDateCloseOffersSwitchOffModal(true);
                    }}
                  />
                  <CustomTooltip text="CREATE_RFP_HARD_DATE_CLOSE_OFFERS_LABEL">
                    <div className="hide-offers-label-icon">
                      <IconLock />
                    </div>
                  </CustomTooltip>
                  <CustomTooltip
                    text={"CREATE_RFP_HARD_DATE_CLOSE_OFFERS_INFO"}
                    addClass="request-head-edit-hide-offers-icon-tooltip"
                  >
                    <div style={{height: "21px"}}>
                      <IconNotificationCircle color={color.black} addClass="mr-0" />
                    </div>
                  </CustomTooltip>
                </div>

                {!(isOffersHideInfo && !values?.offersHidden) && (
                  <div className="main-fields_wrapper__input hide-offers">
                    {isOffersHideInfo ? (
                      <IconCheck color={color.green} />
                    ) : (
                      <CustomRadioSwitch
                        name="offersHidden"
                        checked={currentRequest?.offersHidden}
                        uppercase
                        small
                        addWrapperClass=""
                        isActive={(checked) => {
                          if (checked) {
                            handleChange({offersHidden: checked});
                            setFieldValue("offersHidden", checked);
                          } else setHideOffersSwitchOffModal(true);
                        }}
                      />
                    )}
                    <CustomTooltip text="CREATE_RFP_HIDE_OFFERS_LABEL">
                      <div className="hide-offers-label-icon">
                        <IconEyeCrossed />
                      </div>
                    </CustomTooltip>
                    <CustomTooltip
                      text={hideOfferTooltipTextEdit}
                      addClass="request-head-edit-hide-offers-icon-tooltip"
                      noTranslate
                    >
                      <div style={{height: "21px"}}>
                        <IconNotificationCircle color={color.black} addClass="mr-0" />
                      </div>
                    </CustomTooltip>
                  </div>
                )}
              </div>
              <div className="toggles">
                {auth.user.ActiveOrganization?.allowPublicRft && (
                  <div className="main-fields_wrapper__input hide-offers">
                    <VideoInstructionLink
                      label="VIDEO_LINK_RFP_EDIT_PUBLIC_RFP_LABEL"
                      link="VIDEO_LINK_RFP_EDIT_PUBLIC_RFP_LINK"
                      uppercase
                      bold
                      addClass="mr-4"
                    />
                    <LockValue
                      canPermissions={["ENABLE_SWITCH_OFF_OPEN_RFT_TOGGLE"]}
                      needLabel
                      addClassIcon="open-rft-switch-locked-icon"
                      needShowModalToSubscribe
                    >
                      <CustomRadioSwitch
                        name="isPublic"
                        checked={currentRequest.isPublic}
                        uppercase
                        small
                        isActive={(checked) => handleChange({isPublic: checked}, isDraft)}
                      />
                    </LockValue>

                    <CustomTooltip text="CREATE_RFP_PUBLIC_RFP_SWITCH_LABEL">
                      <div className="df mr-1">
                        <IconBullHorn />
                      </div>
                    </CustomTooltip>
                    <CustomTooltip text="CREATE_RFP_PUBLIC_RFP_SELECT_LABEL">
                      <div className="df">
                        <IconRefresh />
                      </div>
                    </CustomTooltip>
                    <MiniSelect
                      list={turnoverMiniSelect}
                      selectedValue={buyer.minRevenue}
                      defaultValue={buyer.minRevenue}
                      addDropdownClass="wdth-75"
                      itemAsObject
                      onChange={(value) => {
                        handleChange({minRevenue: value}, isDraft);
                        setFieldValue("minRevenue", value);
                      }}
                      informationText="CREATE_RFP_PUBLIC_RFP_BETA_INFO_LABEL"
                    />
                  </div>
                )}
              </div>
            </div>
            {isRemindersModal && (
              <RemindersModal
                reminderDays={values?.reminderDays ?? []}
                closedAt={values?.closedAt}
                onOk={(days) => {
                  setFieldValue("reminderDays", days);
                  handleChange({reminderDays: days});
                }}
                onClose={() => setRemindersModal(false)}
              />
            )}
            {isHardDateCloseOffersSwitchOffModal && (
              <OffersSwitchOffModal
                title="MODAL_HARD_DATE_SWITCH_TITLE"
                description="MODAL_HARD_DATE_CLOSE_OFFERS_SWITCH_DESCRIPTION"
                onOk={() => {
                  setFieldValue("hardDateCloseOffers", false);
                  handleChange({hardDateCloseOffers: false});
                }}
                onClose={() => setHardDateCloseOffersSwitchOffModal(false)}
              />
            )}
            {isHideOffersSwitchOffModal && (
              <OffersSwitchOffModal
                title="MODAL_HIDE_OFFERS_SWITCH_TITLE"
                description="MODAL_HIDE_OFFERS_SWITCH_DESCRIPTION"
                needAttention
                onOk={() => {
                  setFieldValue("offersHidden", false);
                  handleChange({offersHidden: false});
                }}
                onClose={() => setHideOffersSwitchOffModal(false)}
              />
            )}
          </Form>
        )}
      </Formik>
    );
  })
);

export default UpperRequestSettingsHeader;
